import React, { useRef, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import ButtonFill from '../ui/ButtonFill';
import gsap from "gsap";
import { useGSAP } from '@gsap/react';

interface GalleryImage {
  Izobrazhenie: {
    url: string;
    alt?: string;
  };
}

interface TextItem {
  text: string;
}

interface HomeTeamProps {
  image: {
    url: string;
    alt?: string;
  };
  gallery: GalleryImage[];
  title: string;
  text: TextItem[];
  btnText: string;
  btnLink: string;
}

const HomeTeam: React.FC<HomeTeamProps> = ({ image, gallery, title, text, btnText, btnLink }) => {
  const [emblaRef] = useEmblaCarousel({ align: 'start' });

  const sectionRef = useRef<HTMLElement | null>(null);
  const imageWrapper = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const textBlockRef = useRef<HTMLDivElement | null>(null);
  const textBlockWrapperRef = useRef<HTMLDivElement | null>(null);
  const imageLeftWrapperRef = useRef<HTMLDivElement | null>(null);
  const imageCenterWrapperRef = useRef<HTMLDivElement | null>(null);
  const imageRightWrapperRef = useRef<HTMLDivElement | null>(null);

  const imageLeftRef = useRef<HTMLDivElement | null>(null);
  const imageCenterRef = useRef<HTMLDivElement | null>(null);
  const imageRightRef = useRef<HTMLDivElement | null>(null);


  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1080px)", () => {
      gsap.fromTo(imageLeftWrapperRef.current,
        { x: "10rem" },
        {
          x: "-10rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.7,
          }
        }
      );

      gsap.fromTo(imageCenterWrapperRef.current,
        { x: "15rem" },
        {
          x: "-15rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.7,
          }
        }
      );

      
      gsap.fromTo(imageRightWrapperRef.current,
        { x: "-5rem" },
        {
          x: "20rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.7,
          }
        }
      );






      gsap.fromTo(imageLeftRef.current,
        { x: "-3rem" },
        {
          x: "3rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.1,
          }
        }
      );

      gsap.fromTo(imageCenterRef.current,
        { x: "-4rem" },
        {
          x: "3rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.1,
          }
        }
      );

      
      gsap.fromTo(imageRightRef.current,
        { x: "3rem" },
        {
          x: "-3rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.1,
          }
        }
      );




      gsap.fromTo(textBlockRef.current, {
        opacity: 0,
        y: '2rem'
        },
          {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: textBlockWrapperRef.current,
          // markers: true,
            start: "top 70%",
        },
      });

      gsap.fromTo(buttonRef.current, {
        opacity: 0,
         y: '2rem'
        },
          {
        opacity: 1,
        duration: 1,
        y: 0,
        delay: 0.3,
        scrollTrigger: {
          trigger: textBlockWrapperRef.current,
          // markers: true,
            start: "top 70%",
        },
      });



      gsap.fromTo(imageWrapper.current,
        { y: "-7rem" },
        {
          y: "7rem",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.1,
          }
        }
      );
    });

    return () => mm.revert();
  }, {scope: sectionRef});

  const reversedGallery = useMemo(
    () => gallery ? [...gallery].reverse() : [],
  [gallery]
  )

  return (
    <section
      ref={sectionRef}
      className="container-max overflow-hidden flex flex-col w-full py-[4rem]  md:py-[6rem] lg:py-[8rem] xl:py-[10rem]"
    >
      <div className='mb-[4rem] lg:mb-[6rem] container-padding container-grid flex-col'>
        <div className=" col-span-5 lg:mb-[8rem] flex items-center justify-center overflow-hidden lg:col-span-8 lg:col-start-3 relative mb-[3.2rem] h-[19.5rem] md:h-auto md:aspect-[2.05] w-full">
          <div ref={imageWrapper} className='h-[120%] w-full relative'>
            <Image
              src={image?.url}
              alt={image?.alt ?? ""}
              fill
              sizes="100vw"
              quality={85}
              className="w-auto object-cover"
            />
          </div>
        </div>

        <div className=' col-span-5 w-full lg:text-center lg:col-span-12 md:flex md:gap-[1.6rem] md:items-end'>
          <div className='w-full'>
            <h3
              ref={titleRef}
              className='mb-[2rem] lg:mb-0 [&>.selected]:italic [&>.selected]:text-dusty-blue-light'
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div className='lg:hidden md:pr-[7rem] md:max-w-[80rem] flex flex-col w-full gap-[1.6rem] mb-[3.2rem] md:mb-0'>
              {text?.map((item, index) => (<p className='text-1' key={index}>{item.text}</p>))}
            </div>
          </div>
            <ButtonFill isLink={true} href={'/o-nas'} className='md:w-fit lg:hidden text-nowrap'>
              {btnText}
              <img className="ml-[1.2rem] -rotate-45" fetchPriority="low" src="/images/arrow_right.svg" alt="Стрелка вправо" />
            </ButtonFill>
        </div>
      </div>

      {/* Large screen images */}
      <div className=' items-center hidden lg:grid container-padding container-grid'>
        <div ref={imageLeftWrapperRef} className='relative overflow-hidden aspect-[0.8/1] col-span-1 w-full'>
          <div ref={imageLeftRef} className="relative h-full  w-[110%]">
            <Image
              src={gallery?.[0]?.Izobrazhenie?.url ?? ""}
              alt={gallery?.[0]?.Izobrazhenie?.alt ?? ""}
              fill
              sizes="300px"
              className="w-auto object-cover"
            />
          </div>2
        </div>

        <div ref={imageCenterWrapperRef} className="relative overflow-hidden will-change-transform col-span-2 col-start-4 aspect-[0.71/1] w-full">
          <div ref={imageCenterRef} className=" relative h-full  w-[110%]">
            <Image
              src={gallery?.[1]?.Izobrazhenie?.url ?? ""}
              alt={gallery?.[1]?.Izobrazhenie?.alt ?? ""}
              fill
              quality={90}
              sizes="500px"
              className="w-auto  object-cover"
            />
          </div>
        </div>


        <div ref={textBlockWrapperRef} className='w-full col-span-4 col-start-7 md:items-end'>
          <div className='w-full mb-[4.4rem]'>
            <div ref={textBlockRef} className='md:pr-[7rem] md:max-w-[80rem] flex flex-col w-full gap-[1.6rem] mb-[3.2rem] md:mb-0'>
              {text?.map((item, index) => (<p className='text-1' key={index}>{item.text}</p>))}
            </div>
          </div>
          <div ref={buttonRef}>
              <ButtonFill isLink={true} href={'/o-nas'} className='md:w-fit text-nowrap'>
                {btnText}
                <img className="ml-[1.2rem] -rotate-45" fetchPriority="low" loading='lazy' src="/images/arrow_right.svg" alt="Стрелка вправо" />
              </ButtonFill>
          </div>

        </div>
        
        <div className='w-full pr-[25%] col-span-2'>
          <div ref={imageRightWrapperRef} className='relative overflow-hidden aspect-[0.7] w-full'>
            <div ref={imageRightRef} className="relative h-full  w-[110%]">
              <Image
                src={gallery?.[2]?.Izobrazhenie?.url ?? ""}
                alt={gallery?.[2]?.Izobrazhenie?.alt ?? ""}
                fill
                sizes="300px"
                className="w-auto object-cover"
              />
            </div>
          </div>

        </div>
      </div>

      {/* Mobile Gallery */}
      <div className='hidden lg:hidden container-padding md:flex gap-[1.6rem]'>
        {reversedGallery?.map((item, index) => (
          <div key={index + 'i'} className="relative aspect-[0.84/1] w-full">
            <Image
              src={item.Izobrazhenie.url}
              alt={item.Izobrazhenie.alt ?? ""}
              fill
              sizes="400px"
              className="w-auto object-cover"
            />
          </div>
        ))}
      </div>

      {/* Embla Carousel for small screens */}
      <div className="embla px-[2rem] md:hidden md:px-[2.4rem] mb-[2.4rem]" ref={emblaRef}>
        <div className="embla__container gap-[1.2rem] md:gap-[1.6rem]">
          {reversedGallery?.map((item, index) => (
            <div key={index} className="embla__slide flex-[0_0_23.8rem]">
              <div className="relative aspect-[0.84/1] w-full">
                <Image
                  src={item?.Izobrazhenie.url}
                  alt={item?.Izobrazhenie.alt ?? ""}
                  fill
                  sizes="300px"
                  className="w-auto object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeTeam;
